/**
 * Dinheiro no True Finance é SEMPRE inteiro em centavos.
 *
 * Nenhum valor monetário trafega como float em nenhum ponto do sistema: nem no
 * banco, nem na API, nem no cálculo de saldo. `0.1 + 0.2 !== 0.3` é o tipo de
 * bug que num sistema financeiro só aparece na reclamação do cliente.
 */

/** Valor monetário inteiro, em centavos. R$ 12,34 → 1234. */
export type Centavos = number;

export const MOEDA = 'BRL' as const;

const formatadorBRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: MOEDA,
});

const formatadorNumero = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * O Intl separa "R$" do número com espaço não-quebrável (U+00A0). Trocamos por
 * espaço comum porque alguns clientes de WhatsApp renderizam o NBSP como lixo.
 * Construído via `new RegExp` para o caractere não aparecer cru no fonte.
 */
const NBSP = new RegExp('\u00A0', 'g');

export function ehCentavosValido(valor: unknown): valor is Centavos {
  return typeof valor === 'number' && Number.isSafeInteger(valor);
}

function assertCentavos(valor: Centavos): void {
  if (!ehCentavosValido(valor)) {
    throw new TypeError(`Valor monetário deve ser inteiro em centavos, recebido: ${valor}`);
  }
}

/** 400000 → "R$ 4.000,00" */
export function formatarBRL(centavos: Centavos): string {
  assertCentavos(centavos);
  return formatadorBRL.format(centavos / 100).replace(NBSP, ' ');
}

/** 400000 → "4.000,00" (sem símbolo, para tabelas e planilhas) */
export function formatarValor(centavos: Centavos): string {
  assertCentavos(centavos);
  return formatadorNumero.format(centavos / 100).replace(NBSP, ' ');
}

/**
 * Converte a representação textual de um número para centavos.
 *
 * Aceita "R$ 1.234,56", "1234,56", "1.200", "4000", "10.50".
 *
 * Desambiguação de ponto e vírgula em PT-BR:
 * - Havendo vírgula, ela é o separador decimal e os pontos são de milhar:
 *   `"1.234,56"` → 123456
 * - Sem vírgula, um único ponto seguido de 1 ou 2 dígitos é decimal:
 *   `"10.50"` → 1050
 * - Sem vírgula, qualquer outro arranjo de pontos é separador de milhar:
 *   `"1.200"` → 120000 · `"1.234.567"` → 123456700
 *
 * Retorna `null` quando o texto não representa um número — quem chama decide o
 * que fazer (no nosso caso, cair para a extração por LLM).
 */
export function parseValorParaCentavos(texto: string): Centavos | null {
  const limpo = texto.trim().replace(/[r$\s]/gi, '');
  if (limpo === '' || !/^-?[\d.,]+$/.test(limpo)) return null;

  const negativo = limpo.startsWith('-');
  const semSinal = negativo ? limpo.slice(1) : limpo;

  let parteInteira: string;
  let parteFracionaria: string;

  if (semSinal.includes(',')) {
    const partes = semSinal.split(',');
    if (partes.length > 2) return null;
    parteInteira = (partes[0] ?? '').replace(/\./g, '');
    parteFracionaria = partes[1] ?? '';
  } else {
    const qtdPontos = semSinal.split('.').length - 1;
    const ultimoPonto = semSinal.lastIndexOf('.');
    const digitosAposPonto = ultimoPonto >= 0 ? semSinal.length - ultimoPonto - 1 : 0;

    if (qtdPontos === 1 && digitosAposPonto >= 1 && digitosAposPonto <= 2) {
      parteInteira = semSinal.slice(0, ultimoPonto);
      parteFracionaria = semSinal.slice(ultimoPonto + 1);
    } else {
      parteInteira = semSinal.replace(/\./g, '');
      parteFracionaria = '';
    }
  }

  if (!/^\d*$/.test(parteInteira) || !/^\d*$/.test(parteFracionaria)) return null;
  if (parteInteira === '' && parteFracionaria === '') return null;

  // Arredonda em vez de truncar: "1,239" → R$ 1,24.
  let centavosFracao: number;
  if (parteFracionaria.length <= 2) {
    centavosFracao = Number(parteFracionaria.padEnd(2, '0'));
  } else {
    const doisPrimeiros = Number(parteFracionaria.slice(0, 2));
    const terceiro = Number(parteFracionaria[2]);
    centavosFracao = terceiro >= 5 ? doisPrimeiros + 1 : doisPrimeiros;
  }

  const total = Number(parteInteira || '0') * 100 + centavosFracao;
  if (!Number.isSafeInteger(total)) return null;

  return negativo ? -total : total;
}

export function somar(...valores: Centavos[]): Centavos {
  const total = valores.reduce((acc, v) => {
    assertCentavos(v);
    return acc + v;
  }, 0);
  if (!Number.isSafeInteger(total)) {
    throw new RangeError('Soma de centavos ultrapassou o inteiro seguro');
  }
  return total;
}

/**
 * Percentual de uma parte sobre o total, arredondado ao inteiro.
 * Retorna 0 quando o total é 0 — evita NaN no relatório.
 */
export function percentual(parte: Centavos, total: Centavos): number {
  if (total === 0) return 0;
  return Math.round((parte / total) * 100);
}

/** Converte para reais em ponto flutuante. Use APENAS para exportação (XLSX/CSV). */
export function centavosParaReais(centavos: Centavos): number {
  assertCentavos(centavos);
  return centavos / 100;
}
